import { atom } from "jotai";
import { atomWithReset } from "jotai/utils";

export interface ColumnData {
  name: string;
  propertyName: string;
  data: Record<string, string>;
  withinSubgroupOrder?: number;
  isNew?: boolean;
  /** Unit of measure for the column (optional) */
  defaultUnit?: string;
  /** Data type of the column (inferred from data) */
  dataType?: "number" | "string";
  /** Python script for calculated columns (optional) */
  pythonScript?: string;
}

export interface ModalState {
  showAddColumn: boolean;
  showDiscard: boolean;
  newColumnName: string;
  addColumnError: string | null;
}

export interface FieldGroupData {
  columns: ColumnData[];
  uwis: string[];
  formattedUwis: string[];
}

export interface GridColumnConfig {
  id: string;
  title: string;
  width: number;
  hasMenu?: boolean;
  themeOverride?: {
    bgCell?: string;
    bgHeader?: string;
    bgHeaderHasFocus?: string;
    bgHeaderHovered?: string;
  };
}

/**
 * Enum for column filter types - determines which rows to show based on cell values
 */
export enum ColumnFilterType {
  /** Show all rows regardless of cell value */
  ShowAll = "ShowAll",
  /** Show only rows that have a value in this column */
  HasValue = "HasValue",
  /** Show only rows that do NOT have a value in this column */
  NoValue = "NoValue"
}

/**
 * Theme colors for filtered column headers - vibrant light green to indicate active filter
 * Uses a green with similar saturation/vibrancy to the primary teal (#00c7b1)
 */
export const FILTER_HEADER_COLORS = {
  bgHeader: "#7ce5b5", // Vibrant light green matching teal saturation
  bgHeaderHasFocus: "#00c349", // Dark green for focus
  bgHeaderHovered: "#5ad783" // Medium green for hover
} as const;

export const fieldGroupDataAtom = atomWithReset<FieldGroupData | null>(null);

export const columnsAtom = atom(
  (get) => get(fieldGroupDataAtom)?.columns ?? [],
  (get, set, newColumns: ColumnData[]) => {
    const current = get(fieldGroupDataAtom);
    if (current) {
      set(fieldGroupDataAtom, { ...current, columns: newColumns });
    }
  }
);

export const uwisAtom = atom((get) => get(fieldGroupDataAtom)?.uwis ?? []);

export const formattedUwisAtom = atom(
  (get) => get(fieldGroupDataAtom)?.formattedUwis ?? []
);

export const changedCellsAtom = atomWithReset<Map<string, Map<string, string>>>(
  new Map()
);

export const columnWidthsAtom = atomWithReset<Map<string, number>>(new Map());

/**
 * Stores active column filters. Key is the column propertyName, value is the filter type.
 * Only columns with non-ShowAll filters are stored in this map.
 */
export const columnFiltersAtom = atomWithReset<Map<string, ColumnFilterType>>(new Map());

/**
 * Core filtering atom that returns indices of UWIs that pass all active filters.
 * Applies AND logic: a UWI must satisfy ALL active column filters to be included.
 *
 * NOTE: This atom only depends on columnsAtom when filters are active.
 * When no filters, it returns null to signal "use all".
 */
const filteredIndicesAtom = atom((get) => {
  const filters = get(columnFiltersAtom);

  // If no filters are active, return null to signal "use all" - avoid mapping
  if (filters.size === 0) {
    return null;
  }

  // Only read columns and uwis when we actually have filters to apply
  const uwis = get(uwisAtom);
  const columns = get(columnsAtom);

  const indices: number[] = [];
  uwis.forEach((uwi, index) => {
    // Check all active filters - must satisfy ALL (AND logic)
    for (const [propertyName, filterType] of filters) {
      if (filterType === ColumnFilterType.ShowAll) continue;

      const column = columns.find((col) => col.propertyName === propertyName);
      if (!column) continue;

      const cellValue = column.data[uwi];
      const hasValue = cellValue !== undefined && cellValue !== null && cellValue !== "";

      if (filterType === ColumnFilterType.HasValue && !hasValue) return;
      if (filterType === ColumnFilterType.NoValue && hasValue) return;
    }
    indices.push(index);
  });

  return indices;
});

/**
 * Atom that partitions indices by population (wells with data appear first, original order preserved).
 * Returns sorted indices or null if no sorting needed.
 */
const sortedIndicesByPopulationAtom = atom((get) => {
  const uwis = get(uwisAtom);
  const columns = get(columnsAtom);

  // Get the indices to sort (either filtered or all)
  const filteredIndices = get(filteredIndicesAtom);
  const indicesToSort =
    filteredIndices === null
      ? Array.from({ length: uwis.length }, (_, i) => i)
      : filteredIndices;

  // If no data columns, return indices as-is
  if (columns.length === 0) {
    return filteredIndices;
  }

  // Partition indices into populated (has at least one value) and unpopulated, preserving order
  const populated: number[] = [];
  const unpopulated: number[] = [];

  indicesToSort.forEach((index) => {
    const uwi = uwis[index];
    const hasAnyValue = columns.some((col) => {
      const cellValue = col.data[uwi];
      return cellValue !== undefined && cellValue !== null && cellValue !== "";
    });

    if (hasAnyValue) {
      populated.push(index);
    } else {
      unpopulated.push(index);
    }
  });

  // Return populated wells first, then unpopulated (both in original order)
  return [...populated, ...unpopulated];
});

/** Filtered UWIs based on active column filters */
export const filteredUwisAtom = atom((get) => {
  const uwis = get(uwisAtom);
  const sortedIndices = get(sortedIndicesByPopulationAtom);

  // No sorting needed
  if (sortedIndices === null) {
    return uwis;
  }

  const result = sortedIndices.map((i) => uwis[i]);
  return result;
});

/** Filtered formatted UWIs matching the filtered uwis */
export const filteredFormattedUwisAtom = atom((get) => {
  const formattedUwis = get(formattedUwisAtom);
  const sortedIndices = get(sortedIndicesByPopulationAtom);

  // No sorting needed
  if (sortedIndices === null) {
    return formattedUwis;
  }

  const result = sortedIndices.map((i) => formattedUwis[i]);
  return result;
});

/**
 * Computed atom that returns the count of filtered vs total UWIs
 */
export const filterCountAtom = atom((get) => {
  const totalUwis = get(uwisAtom);
  const filteredUwis = get(filteredUwisAtom);
  return {
    filtered: filteredUwis.length,
    total: totalUwis.length,
    isFiltered: filteredUwis.length !== totalUwis.length
  };
});

export const modalStateAtom = atomWithReset<ModalState>({
  showAddColumn: false,
  showDiscard: false,
  newColumnName: "",
  addColumnError: null
});

// Computed atoms (read-only)
export const hasChangesAtom = atom((get) => {
  const changedCells = get(changedCellsAtom);
  const columns = get(columnsAtom);

  // Has edits
  if (changedCells.size > 0) return true;

  // Has new columns (check isNew flag)
  const hasNewColumns = columns.some((col) => col.isNew === true);
  if (hasNewColumns) return true;

  return false;
});

export const gridColumnsAtom = atom((get): GridColumnConfig[] => {
  const columns = get(columnsAtom);
  const widths = get(columnWidthsAtom);
  const filters = get(columnFiltersAtom);

  return [
    {
      id: "uwi",
      title: "UWI",
      width: widths.get("uwi") || 250
    },
    ...columns.map((col) => {
      const hasFilter =
        filters.has(col.propertyName) &&
        filters.get(col.propertyName) !== ColumnFilterType.ShowAll;
      return {
        id: col.name,
        title: col.name,
        width: widths.get(col.name) || 150,
        hasMenu: true, // Enable menu on all data columns for filtering
        themeOverride: col.isNew
          ? {
              bgCell: "#e0f2fe",
              bgHeader: "#7dd3fc",
              bgHeaderHasFocus: "#5ab0daff",
              bgHeaderHovered: "#38bdf8"
            }
          : hasFilter
          ? FILTER_HEADER_COLORS
          : undefined
      };
    })
  ];
});

/**
 * Atom to manage FieldGroupEditor visibility and which field group to edit.
 * Used for navigation from PythonScriptColumn "Edit Field Group" button.
 * Uses atomWithReset to prevent state persistence across sessions.
 */
export const fieldGroupEditorStateAtom = atomWithReset<{
  visible: boolean;
  fieldGroup: string | null;
}>({ visible: false, fieldGroup: null });

/**
 * Atom to signal when a file has been successfully uploaded via UserDefinedColumnEditWizard.
 * Triggers a refetch in FieldGroupEditor.
 */
export const columnUploadedSignalAtom = atom<number>(0);
